import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileCheck,
  FileDown,
  FileText,
  History,
  Info,
  PenLine,
  RefreshCw,
} from 'lucide-react';
import { getThemeColor } from '../../config/appConfig';
import NewtonsCradleLoader from '../../components/common/NewtonsCradleLoader';
import apiService from '../../utils/apiService';
import { useToast } from '../../contexts/ToastContext';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import AgreementPdfModal from './AgreementPdfModal';
import SignAgreementModal from './SignAgreementModal';
import {
  AGREEMENTS_PATH,
  agreementDetailsPath,
  agreementStatusView,
  agreementTitle,
  formatAgreementDate,
  formatAgreementDateTime,
  normalizeAgreementDetails,
  normalizeAgreementList,
  signatureStatusView,
  signedFileNameOf,
  saveBlob,
} from '../../utils/partnerAgreements';

const THEME = getThemeColor('primary');

const Badge = ({ view }) => (
  <span className={`inline-block whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full ${view.tone}`}>
    {view.label}
  </span>
);

const SectionLoader = ({ className = 'py-10' }) => (
  <div className={`flex items-center justify-center ${className}`}>
    <NewtonsCradleLoader size="28px" />
  </div>
);

const ErrorCard = ({ title, message, onRetry, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
    <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-red-600" />
    <p className="text-sm font-semibold text-gray-900">{title}</p>
    {message ? <p className="text-sm text-gray-500 mt-1">{message}</p> : null}
    <div className="mt-4 flex flex-wrap justify-center gap-3">
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-semibold hover:bg-red-50 transition"
          style={{ borderColor: THEME, color: THEME }}
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      ) : null}
      {children}
    </div>
  </div>
);

const IconAction = ({ icon: Icon, label, onClick, busy, primary }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    title={label}
    aria-label={label}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border transition disabled:opacity-50 ${
      primary ? 'text-white border-transparent hover:opacity-90' : 'border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
    style={primary ? { backgroundColor: THEME } : undefined}
  >
    {busy ? <NewtonsCradleLoader size="14px" color={primary ? '#fff' : undefined} /> : <Icon className="w-4 h-4" />}
  </button>
);

const ActionButton = ({ icon: Icon, label, onClick, busy, primary }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className={
      primary
        ? 'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50'
        : 'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50'
    }
    style={primary ? { backgroundColor: THEME } : { borderColor: THEME, color: THEME }}
  >
    {busy ? <NewtonsCradleLoader size="16px" color={primary ? '#fff' : undefined} /> : <Icon className="w-4 h-4" />}
    {label}
  </button>
);

/** View / download / sign actions shared by the list and details screens. */
const useAgreementActions = (onChanged) => {
  const { showError } = useToast();
  const [pdfModal, setPdfModal] = useState(null);
  const [signTarget, setSignTarget] = useState(null);
  const [downloading, setDownloading] = useState('');

  const viewOriginal = useCallback((agreement) => {
    const id = agreement.affiliateAgreementMappingId;
    setPdfModal({
      title: agreementTitle(agreement),
      subtitle: 'Agreement',
      fileName: agreement.agreementFileName,
      loadPdf: () => apiService.getAgreementPdf(id, { mode: 'view' }),
    });
  }, []);

  const viewSigned = useCallback((agreement, fileName) => {
    const id = agreement.affiliateAgreementMappingId;
    setPdfModal({
      title: agreementTitle(agreement),
      subtitle: 'Signed Agreement',
      fileName,
      loadPdf: () => apiService.getAgreementPdf(id, { signed: true, mode: 'view' }),
    });
  }, []);

  const download = useCallback(
    async (agreement, { signed = false, fileName }) => {
      const key = `${agreement.affiliateAgreementMappingId}:${signed ? 'signed' : 'original'}`;
      setDownloading(key);
      try {
        const blob = await apiService.getAgreementPdf(agreement.affiliateAgreementMappingId, { signed, mode: 'download' });
        saveBlob(blob, fileName);
      } catch (error) {
        showError(error.message || 'Download failed. Please try again.');
      } finally {
        setDownloading('');
      }
    },
    [showError]
  );

  const isDownloading = (agreement, signed) =>
    downloading === `${agreement.affiliateAgreementMappingId}:${signed ? 'signed' : 'original'}`;

  const modals = (
    <>
      <AgreementPdfModal
        isOpen={Boolean(pdfModal)}
        title={pdfModal?.title}
        subtitle={pdfModal?.subtitle}
        fileName={pdfModal?.fileName}
        loadPdf={pdfModal?.loadPdf}
        onClose={() => setPdfModal(null)}
      />
      <SignAgreementModal
        agreement={signTarget}
        isOpen={Boolean(signTarget)}
        onClose={() => {
          setSignTarget(null);
          onChanged?.();
        }}
        onSigned={(details) => {
          setSignTarget(null);
          onChanged?.(details);
        }}
      />
    </>
  );

  return { viewOriginal, viewSigned, download, isDownloading, sign: setSignTarget, modals };
};

// ---------------------------------------------------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------------------------------------------------

const AgreementList = () => {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const fetchAgreements = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    const res = await apiService.getMyAgreements();
    if (cancelled()) return;
    if (res.success) setAgreements(normalizeAgreementList(res.data));
    else setError(res.message || res.error || 'Unable to load agreements');
    setLoading(false);
  }, []);

  useFetchOnceByKey(`agreements-${reloadKey}`, fetchAgreements, true);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const actions = useAgreementActions(reload);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <SectionLoader />
      </div>
    );
  }

  if (error) return <ErrorCard title="Failed to load agreements" message={error} onRetry={reload} />;

  if (agreements.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-3">
          <FileText className="w-6 h-6" style={{ color: THEME }} />
        </div>
        <p className="text-sm font-semibold text-gray-900">No agreements yet</p>
        <p className="text-sm text-gray-500 mt-1">Agreements assigned to you will appear here.</p>
      </div>
    );
  }

  const actionRequired = agreements.filter((a) => a.canSign);

  return (
    <div className="space-y-4">
      {actionRequired.map((agreement) => (
        <div
          key={`action-${agreement.affiliateAgreementMappingId}`}
          className="bg-white rounded-xl border border-amber-200 p-5 sm:p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3.5 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                <PenLine className="w-6 h-6 text-amber-700" />
              </div>
              <div className="min-w-0">
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  Action Required
                </span>
                <h3 className="text-base font-bold text-gray-900 mt-1.5">{agreementTitle(agreement)}</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {signatureStatusView(agreement).label} · Expires {formatAgreementDate(agreement.expiryDate)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <ActionButton icon={Eye} label="View" onClick={() => actions.viewOriginal(agreement)} />
              <ActionButton icon={PenLine} label="Review & Sign" primary onClick={() => actions.sign(agreement)} />
            </div>
          </div>
        </div>
      ))}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 sm:px-6 pt-5 pb-3">
          <h3 className="text-base font-bold text-gray-900">Partner Agreements</h3>
          <p className="text-sm text-gray-500 mt-0.5">Agreements assigned to you, including previous versions</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead>
              <tr className="text-left text-xs font-medium border-b border-gray-100 text-gray-500">
                <th className="px-5 py-3">Agreement Name</th>
                <th className="px-3 py-3">Version</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Effective Date</th>
                <th className="px-3 py-3">Expiry Date</th>
                <th className="px-3 py-3">Signature Required</th>
                <th className="px-3 py-3">Signature Status</th>
                <th className="px-3 py-3">Assigned Date</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {agreements.map((agreement) => {
                const signedName = agreement.signedDocumentFileName;
                return (
                  <tr key={agreement.affiliateAgreementMappingId} className="hover:bg-gray-50/80">
                    <td className="px-5 py-3.5 text-sm">
                      <button
                        type="button"
                        onClick={() => navigate(agreementDetailsPath(agreement.affiliateAgreementMappingId))}
                        className="font-semibold text-gray-900 hover:text-red-800 text-left"
                      >
                        {agreement.agreementName}
                      </button>
                      {agreement.isCurrent && agreement.isLatestVersion ? (
                        <span className="ml-2 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                          Current
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-3.5 text-sm text-gray-900">v{agreement.agreementVersion}</td>
                    <td className="px-3 py-3.5"><Badge view={agreementStatusView(agreement)} /></td>
                    <td className="px-3 py-3.5 text-sm text-gray-900 whitespace-nowrap">{formatAgreementDate(agreement.effectiveDate)}</td>
                    <td className="px-3 py-3.5 text-sm text-gray-900 whitespace-nowrap">{formatAgreementDate(agreement.expiryDate)}</td>
                    <td className="px-3 py-3.5 text-sm text-gray-900">{agreement.requiresSignature ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-3.5"><Badge view={signatureStatusView(agreement)} /></td>
                    <td className="px-3 py-3.5 text-sm text-gray-900 whitespace-nowrap">{formatAgreementDate(agreement.assignedDateTime)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <IconAction icon={Eye} label="View Agreement" onClick={() => actions.viewOriginal(agreement)} />
                        <IconAction
                          icon={Download}
                          label="Download Agreement"
                          busy={actions.isDownloading(agreement, false)}
                          onClick={() => actions.download(agreement, { fileName: agreement.agreementFileName })}
                        />
                        {agreement.canSign ? (
                          <IconAction icon={PenLine} label="Sign Agreement" primary onClick={() => actions.sign(agreement)} />
                        ) : null}
                        {agreement.hasSignedDocument ? (
                          <>
                            <IconAction icon={FileCheck} label="View Signed Agreement" onClick={() => actions.viewSigned(agreement, signedName)} />
                            <IconAction
                              icon={FileDown}
                              label="Download Signed Agreement"
                              busy={actions.isDownloading(agreement, true)}
                              onClick={() => actions.download(agreement, { signed: true, fileName: signedName })}
                            />
                          </>
                        ) : null}
                        <IconAction
                          icon={ChevronRight}
                          label="Agreement Details"
                          onClick={() => navigate(agreementDetailsPath(agreement.affiliateAgreementMappingId))}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {actions.modals}
    </div>
  );
};

// ---------------------------------------------------------------------------------------------------------------------
// Details
// ---------------------------------------------------------------------------------------------------------------------

const DetailField = ({ label, children }) => (
  <div className="space-y-1.5">
    <p className="text-xs text-gray-500">{label}</p>
    <div className="text-sm font-semibold text-gray-900 break-words">{children || '—'}</div>
  </div>
);

const BackLink = () => (
  <Link to={AGREEMENTS_PATH} className="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-red-800">
    <ChevronLeft className="w-4 h-4" />
    All Agreements
  </Link>
);

const AgreementDetails = ({ mappingId }) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const validId = /^\d+$/.test(String(mappingId)) && Number(mappingId) > 0;

  const fetchDetails = useCallback(
    async ({ cancelled }) => {
      setLoading(true);
      setError('');
      const res = await apiService.getMyAgreementById(mappingId);
      if (cancelled()) return;
      const parsed = res.success ? normalizeAgreementDetails(res.data) : null;
      if (parsed) setDetails(parsed);
      else {
        setDetails(null);
        setError(res.message || res.error || 'Agreement not found');
      }
      setLoading(false);
    },
    [mappingId]
  );

  useFetchOnceByKey(validId ? `agreement-${mappingId}-${reloadKey}` : null, fetchDetails, validId);
  const actions = useAgreementActions((signedDetails) => {
    if (signedDetails) setDetails(signedDetails);
    else setReloadKey((k) => k + 1);
  });

  if (!validId) {
    return (
      <ErrorCard title="Invalid agreement" message="The agreement link is not valid.">
        <BackLink />
      </ErrorCard>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <SectionLoader />
      </div>
    );
  }

  if (!details) {
    const notFound = /not found|invalid/i.test(error);
    return (
      <ErrorCard
        title={notFound ? 'Agreement not found' : 'Failed to load agreement'}
        message={notFound ? 'This agreement does not exist or is not assigned to you.' : error}
        onRetry={notFound ? undefined : () => setReloadKey((k) => k + 1)}
      >
        <BackLink />
      </ErrorCard>
    );
  }

  const { agreement, signature, history } = details;
  const signed = agreement.signatureStatus === 'Signed';
  const signedFileName = signedFileNameOf(details);

  return (
    <div className="space-y-4">
      <BackLink />

      {agreement.canSign ? (
        <div className="bg-white rounded-xl border border-amber-200 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
              <PenLine className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Action Required</span>
              <h3 className="text-base font-bold text-gray-900 mt-1.5">{agreementTitle(agreement)}</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {signatureStatusView(agreement).label}. Please review the Agreement and complete your signature.
              </p>
            </div>
          </div>
          <ActionButton icon={PenLine} label="Review & Sign" primary onClick={() => actions.sign(agreement)} />
        </div>
      ) : signed ? (
        <div className="bg-white rounded-xl border border-emerald-200 p-5 sm:p-6 flex items-start gap-3.5">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Completed</span>
            <h3 className="text-base font-bold text-gray-900 mt-1.5">{agreementTitle(agreement)}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Signature Status: Signed · Signed Date: {formatAgreementDateTime(signature?.signedDateTime || agreement.signedDateTime)}
            </p>
          </div>
        </div>
      ) : agreement.signBlockedReason ? (
        <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-gray-500" />
          <p className="text-sm text-gray-600">{agreement.signBlockedReason}. This version is available as read-only.</p>
        </div>
      ) : null}

      <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h3 className="text-base font-bold text-gray-900">{agreement.agreementName}</h3>
          <Badge view={agreementStatusView(agreement)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <DetailField label="Agreement Name">{agreement.agreementName}</DetailField>
          <DetailField label="Agreement Version">{agreement.agreementVersion}</DetailField>
          <DetailField label="Agreement Status">{agreementStatusView(agreement).label}</DetailField>
          <DetailField label="Signature Status"><Badge view={signatureStatusView(agreement)} /></DetailField>
          <DetailField label="Effective Date">{formatAgreementDate(agreement.effectiveDate)}</DetailField>
          <DetailField label="Expiry Date">{formatAgreementDate(agreement.expiryDate)}</DetailField>
          <DetailField label="Signature Required">{agreement.requiresSignature ? 'Yes' : 'No'}</DetailField>
          <DetailField label="Assigned Date">{formatAgreementDate(agreement.assignedDateTime)}</DetailField>
        </div>

        <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-gray-100">
          <ActionButton icon={Eye} label="View Agreement" onClick={() => actions.viewOriginal(agreement)} />
          <ActionButton
            icon={Download}
            label="Download Agreement"
            busy={actions.isDownloading(agreement, false)}
            onClick={() => actions.download(agreement, { fileName: agreement.agreementFileName })}
          />
          {agreement.canSign ? (
            <ActionButton icon={PenLine} label="Review & Sign Agreement" primary onClick={() => actions.sign(agreement)} />
          ) : null}
          {agreement.hasSignedDocument ? (
            <>
              <ActionButton icon={FileCheck} label="View Signed Agreement" onClick={() => actions.viewSigned(agreement, signedFileName)} />
              <ActionButton
                icon={FileDown}
                label="Download Signed Agreement"
                busy={actions.isDownloading(agreement, true)}
                onClick={() => actions.download(agreement, { signed: true, fileName: signedFileName })}
              />
            </>
          ) : null}
        </div>
      </div>

      {signature ? (
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">Signature</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <DetailField label="Signed By">{signature.signerName}</DetailField>
            <DetailField label="Email">{signature.signerEmail}</DetailField>
            <DetailField label="Signed Date">{formatAgreementDateTime(signature.signedDateTime)}</DetailField>
            <DetailField label="Signature Reference">{signature.signatureReference}</DetailField>
          </div>
        </div>
      ) : null}

      {history.length > 1 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4" style={{ color: THEME }} />
            <h3 className="text-base font-bold text-gray-900">Agreement History</h3>
          </div>
          <ul className="divide-y divide-gray-100">
            {history.map((item) => {
              const isSelected = item.affiliateAgreementMappingId === agreement.affiliateAgreementMappingId;
              return (
                <li key={item.affiliateAgreementMappingId}>
                  <button
                    type="button"
                    onClick={() => navigate(agreementDetailsPath(item.affiliateAgreementMappingId))}
                    disabled={isSelected}
                    className="w-full flex flex-wrap items-center gap-2 py-3 text-left disabled:cursor-default"
                  >
                    <span className={`text-sm font-semibold ${isSelected ? 'text-red-800' : 'text-gray-900'}`}>
                      v{item.agreementVersion}
                    </span>
                    <Badge view={agreementStatusView(item)} />
                    {item.isCurrent && item.isLatestVersion ? (
                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Current</span>
                    ) : null}
                    <Badge view={signatureStatusView(item)} />
                    <span className="text-xs text-gray-500 ml-auto">Assigned {formatAgreementDate(item.assignedDateTime)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {actions.modals}
    </div>
  );
};

const PartnerAgreements = ({ mappingId }) =>
  mappingId ? <AgreementDetails key={mappingId} mappingId={mappingId} /> : <AgreementList />;

export default PartnerAgreements;
